/**
 * AdminBibleDashboard - Bible module administration
 * - Monitor Bible import progress
 * - Manage reading plans
 * - Send test reminders
 * - View chapter completion by book
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DashboardLayout } from '@/components/layouts';
import { useAuthStore } from '@/lib/store';

// UI Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

// Icons
import { 
  BookOpen, RefreshCw, Send, CheckCircle2, XCircle, 
  Clock, Bell, Calendar, Users, Loader2, Play, 
  BookMarked, ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// API helpers
const bibleAPI = {
  getImportStatus: async (token) => {
    const res = await fetch(`${API_URL}/api/admin/bible/import-status`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Failed to fetch import status');
    return res.json();
  },
  startBulkImport: async (token, startBook) => {
    const url = startBook 
      ? `${API_URL}/api/admin/bible/bulk-import?start_book=${encodeURIComponent(startBook)}`
      : `${API_URL}/api/admin/bible/bulk-import`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Failed to start import');
    return res.json();
  },
  importChapter: async (token, book, chapter) => {
    const res = await fetch(
      `${API_URL}/api/admin/bible/import-chapter?book=${encodeURIComponent(book)}&chapter=${chapter}`,
      { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }}
    );
    if (!res.ok) throw new Error('Failed to import chapter');
    return res.json();
  },
  getPlans: async () => {
    const res = await fetch(`${API_URL}/api/bible/plans`);
    if (!res.ok) throw new Error('Failed to fetch plans');
    return res.json();
  },
  sendTestReminder: async (token, email) => {
    const url = email 
      ? `${API_URL}/api/bible/test-reminder?email=${encodeURIComponent(email)}`
      : `${API_URL}/api/bible/test-reminder`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Failed to send test reminder');
    return res.json();
  }
};

// ============== Import Status Card ==============
const ImportStatusCard = ({ token }) => {
  const queryClient = useQueryClient();
  
  const { data: status, isLoading, refetch } = useQuery({
    queryKey: ['bible-import-status'],
    queryFn: () => bibleAPI.getImportStatus(token),
    refetchInterval: 10000 // Refresh every 10 seconds
  });

  const bulkImportMutation = useMutation({
    mutationFn: (startBook) => bibleAPI.startBulkImport(token, startBook),
    onSuccess: () => {
      toast.success('Bible import started in background');
      queryClient.invalidateQueries(['bible-import-status']);
    },
    onError: (err) => toast.error(err.message)
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-8 w-full" />
        </CardContent>
      </Card>
    );
  }

  const percentComplete = status?.percent_complete || 0;
  const isComplete = percentComplete >= 100;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Bible Import Status</CardTitle>
          </div>
          <Button variant="ghost" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <CardDescription>
          KJV Bible text from bible-api.com
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">
              {status?.imported_chapters || 0} / {status?.total_chapters || 1189} chapters
            </span>
          </div>
          
          <Progress value={percentComplete} className="h-3" />
          
          <div className="flex items-center justify-between">
            <Badge variant={isComplete ? "default" : "secondary"} className="gap-1">
              {isComplete ? (
                <>
                  <CheckCircle2 className="h-3 w-3" />
                  Complete
                </>
              ) : (
                <>
                  <Clock className="h-3 w-3" />
                  {percentComplete.toFixed(1)}% Complete
                </>
              )}
            </Badge>
            
            {!isComplete && (
              <Button 
                size="sm" 
                onClick={() => bulkImportMutation.mutate()}
                disabled={bulkImportMutation.isPending}
              >
                {bulkImportMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                Continue Import
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============== Books Progress Table ==============
const BooksProgressTable = ({ token }) => {
  const [selectedBook, setSelectedBook] = useState(null);
  const queryClient = useQueryClient();
  
  const { data: status, isLoading } = useQuery({
    queryKey: ['bible-import-status'],
    queryFn: () => bibleAPI.getImportStatus(token)
  });

  const importChapterMutation = useMutation({
    mutationFn: ({ book, chapter }) => bibleAPI.importChapter(token, book, chapter),
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries(['bible-import-status']);
    },
    onError: (err) => toast.error(err.message)
  });

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  const books = status?.books || [];
  const oldTestament = books.filter(b => 
    ['Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy', 'Joshua', 'Judges', 
     'Ruth', '1 Samuel', '2 Samuel', '1 Kings', '2 Kings', '1 Chronicles', '2 Chronicles',
     'Ezra', 'Nehemiah', 'Esther', 'Job', 'Psalms', 'Proverbs', 'Ecclesiastes', 
     'Song of Solomon', 'Isaiah', 'Jeremiah', 'Lamentations', 'Ezekiel', 'Daniel',
     'Hosea', 'Joel', 'Amos', 'Obadiah', 'Jonah', 'Micah', 'Nahum', 'Habakkuk',
     'Zephaniah', 'Haggai', 'Zechariah', 'Malachi'].includes(b.name)
  );
  const newTestament = books.filter(b => !oldTestament.includes(b));

  const renderBookRow = (book) => (
    <TableRow 
      key={book.name} 
      className={`cursor-pointer hover:bg-muted/50 ${book.complete ? 'bg-green-50/50' : ''}`}
      onClick={() => setSelectedBook(book)}
    >
      <TableCell className="font-medium">{book.name}</TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Progress 
            value={(book.imported / book.total) * 100} 
            className="h-2 w-20"
          />
          <span className="text-xs text-muted-foreground">
            {book.imported}/{book.total}
          </span>
        </div>
      </TableCell>
      <TableCell>
        {book.complete ? (
          <CheckCircle2 className="h-4 w-4 text-green-500" />
        ) : (
          <XCircle className="h-4 w-4 text-amber-500" />
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BookMarked className="h-5 w-5" />
            Books Progress
          </CardTitle>
          <CardDescription>Click a book to import missing chapters</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="ot">
            <TabsList className="mb-4">
              <TabsTrigger value="ot">Old Testament ({oldTestament.length})</TabsTrigger>
              <TabsTrigger value="nt">New Testament ({newTestament.length})</TabsTrigger>
            </TabsList>
            
            <TabsContent value="ot">
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Book</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {oldTestament.map(renderBookRow)}
                  </TableBody>
                </Table>
              </ScrollArea>
            </TabsContent>
            
            <TabsContent value="nt">
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Book</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {newTestament.map(renderBookRow)}
                  </TableBody>
                </Table>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Book Detail Dialog */}
      <Dialog open={!!selectedBook} onOpenChange={() => setSelectedBook(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedBook?.name}</DialogTitle>
            <DialogDescription>
              {selectedBook?.imported} of {selectedBook?.total} chapters imported
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Progress 
              value={(selectedBook?.imported / selectedBook?.total) * 100} 
              className="h-3 mb-4"
            />
            
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-10 gap-1">
              {Array.from({ length: selectedBook?.total || 0 }, (_, i) => i + 1).map(chapter => {
                const isImported = chapter <= (selectedBook?.imported || 0);
                return (
                  <Button
                    key={chapter}
                    variant={isImported ? "default" : "outline"}
                    size="sm"
                    className="h-8 w-8 p-0"
                    disabled={isImported || importChapterMutation.isPending}
                    onClick={() => importChapterMutation.mutate({ 
                      book: selectedBook.name, 
                      chapter 
                    })}
                  >
                    {chapter}
                  </Button>
                );
              })}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedBook(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ============== Reading Plans Card ==============
const ReadingPlansCard = () => {
  const { data: plans, isLoading } = useQuery({
    queryKey: ['bible-reading-plans'],
    queryFn: bibleAPI.getPlans
  });

  if (isLoading) {
    return <Skeleton className="h-48 w-full" />;
  }

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-amber-100 text-amber-800';
      case 'challenging': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Reading Plans
        </CardTitle>
        <CardDescription>{plans?.length || 0} plans available</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {plans?.map(plan => (
            <div 
              key={plan.id || plan._id} 
              className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
            >
              <div className="flex-1">
                <h4 className="font-medium text-sm">{plan.title}</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {plan.duration_days} days • {plan.category}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge 
                  variant="secondary" 
                  className={`text-xs ${getDifficultyColor(plan.difficulty)}`}
                >
                  {plan.difficulty}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  <Users className="h-3 w-3 mr-1" />
                  {plan.subscribers_count || 0}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ============== Test Reminder Card ==============
const TestReminderCard = ({ token }) => {
  const [email, setEmail] = useState('');
  
  const sendReminderMutation = useMutation({
    mutationFn: () => bibleAPI.sendTestReminder(token, email || undefined),
    onSuccess: (data) => {
      if (data.details?.success) {
        toast.success('Test reminder sent successfully!');
      } else {
        toast.error(data.details?.error || 'Failed to send reminder');
      }
    },
    onError: (err) => toast.error(err.message)
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Test Reminder
        </CardTitle>
        <CardDescription>Send a test Bible reading reminder email</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="test-email">Email Address (optional)</Label>
            <Input
              id="test-email"
              type="email"
              placeholder="Leave empty to use your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          
          <Button 
            className="w-full"
            onClick={() => sendReminderMutation.mutate()}
            disabled={sendReminderMutation.isPending}
          >
            {sendReminderMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Send Test Reminder
          </Button>
          
          <p className="text-xs text-muted-foreground">
            This will send a sample daily reading reminder email with verse of the day 
            and today's reading passages.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

// ============== Quick Stats ==============
const QuickStats = ({ token }) => {
  const { data: status } = useQuery({
    queryKey: ['bible-import-status'],
    queryFn: () => bibleAPI.getImportStatus(token)
  });
  
  const { data: plans } = useQuery({
    queryKey: ['bible-reading-plans'],
    queryFn: bibleAPI.getPlans
  });

  const stats = [
    {
      label: 'Chapters Imported',
      value: status?.imported_chapters || 0,
      total: status?.total_chapters || 1189,
      icon: BookOpen,
      color: 'text-blue-500'
    },
    {
      label: 'Reading Plans',
      value: plans?.length || 0,
      icon: Calendar,
      color: 'text-purple-500'
    },
    {
      label: 'Books Complete',
      value: status?.books?.filter(b => b.complete).length || 0,
      total: 66,
      icon: CheckCircle2,
      color: 'text-green-500'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      {stats.map((stat, i) => (
        <Card key={i}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
                <p className="text-2xl font-bold">
                  {stat.value}
                  {stat.total && (
                    <span className="text-sm font-normal text-muted-foreground">
                      /{stat.total}
                    </span>
                  )}
                </p>
              </div>
              <stat.icon className={`h-8 w-8 ${stat.color}`} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ============== Main Dashboard ==============
const AdminBibleDashboard = () => {
  const { token } = useAuthStore();
  
  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BookOpen className="h-6 w-6" />
              Bible Administration
            </h1>
            <p className="text-muted-foreground">
              Manage Bible content, reading plans, and reminders
            </p>
          </div>
          <Button variant="outline" asChild>
            <a href="/admin">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </a>
          </Button>
        </div>

        {/* Quick Stats */}
        <QuickStats token={token} />

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Import Status & Books */}
          <div className="lg:col-span-2 space-y-6">
            <ImportStatusCard token={token} />
            <BooksProgressTable token={token} />
          </div>

          {/* Right Column - Plans & Reminders */}
          <div className="space-y-6">
            <ReadingPlansCard />
            <TestReminderCard token={token} />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminBibleDashboard;
